import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { generateMnemonic, validateMnemonic } from '../utils/bip39';

function MnemonicReveal({ data, onConfirm, loading }) {
  const words = data.phrase.split(' ');
  const [copied,    setCopied]    = useState(false);
  const [copyError, setCopyError] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(data.phrase);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyError(true);
      setTimeout(() => setCopyError(false), 2000);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '.5rem' }}>🔑</div>
          <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--gold-light)', margin: '0 0 .6rem' }}>
            Vos 12 mots secrets
          </h2>
          <p style={{ fontSize: '.82rem', color: 'var(--crimson)', lineHeight: 1.55, margin: 0 }}>
            Notez ces mots — ils sont votre seul mot de passe.<br />
            Impossible de les récupérer plus tard.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '.4rem', marginBottom: '1.5rem' }}>
          {words.map((word, i) => (
            <div key={i} style={{
              background: 'rgba(201,168,76,.1)', border: '1px solid rgba(201,168,76,.28)',
              borderRadius: 8, padding: '.45rem .35rem', textAlign: 'center',
            }}>
              <div style={{ fontSize: '.58rem', color: 'var(--gray-400)', marginBottom: '.12rem' }}>{i + 1}.</div>
              <div style={{ fontFamily: 'var(--font-display)', color: 'var(--gold-light)', fontSize: '.82rem', fontWeight: 700 }}>
                {word}
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="btn btn-ghost w-full"
          onClick={handleCopy}
          style={{ marginBottom: '.75rem', color: copied ? 'var(--color-text-success, #4caf50)' : undefined }}
        >
          {copied ? '✓ Copié !' : copyError ? 'Copie manuelle nécessaire' : '📋 Copier les 12 mots'}
        </button>

        <button className="btn btn-primary w-full" onClick={onConfirm} disabled={loading}>
          {loading ? '...' : "J'ai noté mes mots, créer mon compte →"}
        </button>
      </div>
    </div>
  );
}

export default function AuthPage() {
  const { login, register, loginBIP39, registerBIP39 } = useAuth();

  const [mode,    setMode]    = useState('login');
  const [subMode, setSubMode] = useState('email');

  const [name,        setName]        = useState('');
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [bip39Name,   setBip39Name]   = useState('');
  const [bip39Phrase, setBip39Phrase] = useState('');

  const [reveal,  setReveal]  = useState(null);
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setName(''); setEmail(''); setPassword('');
    setBip39Name(''); setBip39Phrase('');
    setError('');
  };

  const switchMode = (next) => { setMode(next); reset(); };
  const switchSub  = (next) => { setSubMode(next); setError(''); };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (mode === 'login') await login(email, password);
      else                   await register(name, email, password);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBip39Submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (mode === 'login') {
        const phrase = bip39Phrase.trim().toLowerCase().replace(/\s+/g, ' ');
        if (!validateMnemonic(phrase)) throw new Error('Phrase invalide. Vérifiez les 12 mots.');
        await loginBIP39(bip39Name.trim(), phrase);
      } else {
        const phrase = await generateMnemonic();
        setReveal({ name: bip39Name.trim(), phrase });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevealConfirm = async () => {
    if (!reveal) return;
    setLoading(true);
    try {
      await registerBIP39(reveal.name, reveal.phrase);
      await loginBIP39(reveal.name, reveal.phrase);
    } catch (err) {
      setError(err.message);
      setReveal(null);
    } finally {
      setLoading(false);
    }
  };

  if (reveal) {
    return <MnemonicReveal data={reveal} onConfirm={handleRevealConfirm} loading={loading} />;
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoWrap}>
          <span style={styles.logoIcon}>📖</span>
          <h1 style={styles.logoTitle}>Manna</h1>
          <p style={styles.logoSub}>Apprends la Bible en jouant</p>
        </div>

        {/* Toggle principal */}
        <div style={styles.mainToggle}>
          {[
            { key: 'login',    label: 'Se connecter'  },
            { key: 'register', label: "S'inscrire"     },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              style={{ ...styles.mainBtn, ...(mode === key ? styles.mainBtnActive : {}) }}
              onClick={() => switchMode(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Sous-onglets */}
        <div style={styles.subTabs}>
          {[
            { key: 'email', label: '📧 Avec email'  },
            { key: 'bip39', label: '🔑 Sans email'  },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              style={{ ...styles.subTab, ...(subMode === key ? styles.subTabActive : {}) }}
              onClick={() => switchSub(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Formulaire email */}
        {subMode === 'email' && (
          <form onSubmit={handleEmailSubmit} style={styles.form}>
            {mode === 'register' && (
              <div style={styles.field}>
                <label style={styles.label}>Prénom ou pseudo</label>
                <input className="game-input" type="text" placeholder="Ex: Marie"
                  value={name} onChange={e => setName(e.target.value)} required autoComplete="name" />
              </div>
            )}
            <div style={styles.field}>
              <label style={styles.label}>Adresse e-mail</label>
              <input className="game-input" type="email" placeholder="ton@email.com"
                value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Mot de passe</label>
              <input className="game-input" type="password" placeholder="••••••••"
                value={password} onChange={e => setPassword(e.target.value)} required minLength={6}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
            </div>
            {error && <p style={styles.error}>{error}</p>}
            <button type="submit" className="btn btn-primary w-full" disabled={loading} style={{ marginTop: '.25rem' }}>
              {loading ? '...' : mode === 'login' ? 'Se connecter' : 'Créer mon compte'}
            </button>
          </form>
        )}

        {/* Formulaire sans email */}
        {subMode === 'bip39' && (
          <form onSubmit={handleBip39Submit} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Pseudo</label>
              <input className="game-input" type="text" placeholder="Ex: Marie"
                value={bip39Name} onChange={e => setBip39Name(e.target.value)} required autoComplete="username" />
            </div>
            {mode === 'login' && (
              <div style={styles.field}>
                <label style={styles.label}>Vos 12 mots (séparés par des espaces)</label>
                <textarea
                  className="game-input"
                  placeholder="mot1 mot2 mot3 … mot12"
                  value={bip39Phrase}
                  onChange={e => setBip39Phrase(e.target.value)}
                  required
                  rows={3}
                  style={{ resize: 'none', fontFamily: 'var(--font-body)', lineHeight: 1.6 }}
                />
              </div>
            )}
            {mode === 'register' && (
              <p style={{ fontSize: '.78rem', color: 'var(--gray-400)', lineHeight: 1.55, margin: 0 }}>
                Entrez votre pseudo. 12 mots secrets seront générés — notez-les bien, ils remplacent votre mot de passe.
              </p>
            )}
            {error && <p style={styles.error}>{error}</p>}
            <button type="submit" className="btn btn-primary w-full" disabled={loading || !bip39Name.trim()} style={{ marginTop: '.25rem' }}>
              {loading ? '...' : mode === 'login' ? 'Se connecter' : 'Générer mes 12 mots'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '1.5rem 1rem',
  },
  card: {
    width: '100%', maxWidth: '400px',
    background: 'rgba(253,246,227,.05)', border: '1px solid rgba(201,168,76,.22)',
    borderRadius: '16px', padding: '2rem 1.75rem', boxShadow: '0 8px 40px rgba(0,0,0,.5)',
  },
  logoWrap:  { textAlign: 'center', marginBottom: '1.75rem' },
  logoIcon:  { fontSize: '2.5rem', display: 'block', marginBottom: '.4rem' },
  logoTitle: { fontFamily: 'var(--font-display)', color: 'var(--gold-light)', fontSize: '1.8rem', margin: '0 0 .25rem' },
  logoSub:   { color: 'var(--gray-400)', fontSize: '.82rem', margin: 0 },
  mainToggle: {
    display: 'flex', gap: '8px', marginBottom: '1.25rem',
  },
  mainBtn: {
    flex: 1, padding: '.6rem', border: '1px solid rgba(201,168,76,.25)', borderRadius: '8px',
    background: 'transparent', color: 'var(--gray-400)',
    fontFamily: 'var(--font-display)', fontSize: '.85rem', cursor: 'pointer', transition: 'all .15s',
  },
  mainBtnActive: {
    background: 'rgba(201,168,76,.18)', borderColor: 'var(--gold-light)', color: 'var(--gold-light)',
  },
  subTabs: {
    display: 'flex', gap: '1.5rem', marginBottom: '1.25rem', borderBottom: '1px solid rgba(255,255,255,.08)', paddingBottom: '.75rem',
  },
  subTab: {
    background: 'none', border: 'none', padding: '0 0 .25rem', cursor: 'pointer',
    fontFamily: 'var(--font-body)', fontSize: '.82rem', color: 'var(--gray-400)', transition: 'all .15s',
  },
  subTabActive: {
    color: 'var(--gold-light)', borderBottom: '2px solid var(--gold-light)',
  },
  form:  { display: 'flex', flexDirection: 'column', gap: '.9rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '.35rem' },
  label: { fontSize: '.8rem', color: 'var(--gray-400)', fontFamily: 'var(--font-body)' },
  error: {
    color: 'var(--crimson)', fontSize: '.82rem',
    background: 'rgba(139,26,26,.12)', border: '1px solid rgba(139,26,26,.3)',
    borderRadius: '6px', padding: '.5rem .75rem', margin: 0,
  },
};
